import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger,
} from "@/components/ui/select";
import { ArrowLeft, Plus, LifeBuoy, GanttChartSquare, ListChecks, Trash2 } from "lucide-react";
import TaskTree from "@/components/TaskTree";
import TaskDialog from "@/components/TaskDialog";
import GanttChart from "@/components/GanttChart";
import { TICKET_STATUSES, PRIORITIES, statusMeta } from "@/lib/taskman";
import { useNavigate } from "react-router-dom";

export default function TicketDetailPage() {
  const { ticketId } = useParams();
  const [ticket, setTicket] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [assignees, setAssignees] = useState({ team: [], client_users: [] });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [parentTask, setParentTask] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/tickets/${ticketId}`);
      setTicket(data.ticket);
      setTasks(data.tasks || []);
      const a = await api.get("/taskman/assignees", {
        params: { client_id: data.ticket.client_id } });
      setAssignees(a.data);
    } catch { toast.error("Failed to load ticket"); }
    finally { setLoading(false); }
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);

  const patchTicket = async (updates) => {
    try {
      await api.patch(`/tickets/${ticketId}`, updates);
      setTicket((t) => ({ ...t, ...updates }));
      toast.success("Ticket updated");
    } catch { toast.error("Update failed"); }
  };

  const removeTicket = async () => {
    if (!window.confirm("Delete this ticket and all its tasks?")) return;
    try {
      await api.delete(`/tickets/${ticketId}?force=true`);
      toast.success("Ticket deleted");
      navigate("/tickets");
    } catch { toast.error("Delete failed"); }
  };

  const saveTask = async (payload) => {
    try {
      if (editing?.task_id) {
        await api.patch(`/tasks/${editing.task_id}`, payload);
      } else {
        payload.ticket_id = ticketId;
        if (parentTask) payload.parent_task_id = parentTask.task_id;
        await api.post("/tasks", payload);
      }
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      throw e;
    }
  };

  const quickStatus = async (task, status) => {
    try {
      await api.patch(`/tasks/${task.task_id}`, { status });
      setTasks((rows) => rows.map((r) => r.task_id === task.task_id ? { ...r, status } : r));
    } catch { toast.error("Update failed"); }
  };

  const deleteTask = async (task) => {
    if (!window.confirm(`Delete "${task.title}" and all its subtasks?`)) return;
    try { await api.delete(`/tasks/${task.task_id}`); load(); }
    catch { toast.error("Delete failed"); }
  };

  const reorder = async (task_ids) => {
    try { await api.post("/tasks/reorder", { task_ids }); load(); }
    catch { toast.error("Reorder failed"); }
  };

  const updateDates = async (taskId, dates) => {
    try {
      await api.patch(`/tasks/${taskId}`, dates);
      setTasks((rows) => rows.map((r) => r.task_id === taskId ? { ...r, ...dates } : r));
    } catch { toast.error("Date update failed"); }
  };

  if (loading) return <div className="p-10 text-center text-sm text-zinc-400">Loading…</div>;
  if (!ticket) return <div className="p-10 text-center text-sm text-zinc-400">Ticket not found.</div>;

  const st = statusMeta(TICKET_STATUSES, ticket.status);
  const pr = statusMeta(PRIORITIES, ticket.priority);

  return (
    <div className="p-6 space-y-4 max-w-[1400px]" data-testid="ticket-detail-page">
      <Link to="/tickets" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800">
        <ArrowLeft className="h-3.5 w-3.5" /> All tickets
      </Link>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-extrabold tracking-tight flex items-center gap-2"
            data-testid="ticket-detail-subject">
            <LifeBuoy className="h-6 w-6 text-[#002FA7]" /> {ticket.subject}
          </h1>
          <div className="text-sm text-zinc-500 mt-1 flex items-center gap-2 flex-wrap">
            <span>{ticket.client_name}</span>
            {ticket.project_id && (
              <Link to={`/projects/${ticket.project_id}`} className="text-xs text-[#002FA7] hover:underline">
                Project: {ticket.project_name}
              </Link>
            )}
            <span className="text-xs text-zinc-400">
              Raised by {ticket.created_by?.name || ticket.created_by?.email || "—"}
              {ticket.created_by?.role === "client" ? " (client)" : ""} · {(ticket.created_at || "").slice(0, 10)}
            </span>
          </div>
          {ticket.description && (
            <p className="text-xs text-zinc-500 mt-2 max-w-2xl whitespace-pre-wrap">{ticket.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Select value={ticket.priority} onValueChange={(v) => patchTicket({ priority: v })}>
            <SelectTrigger className="w-28 h-8" data-testid="ticket-priority-quick">
              <span className={`inline-flex px-1.5 py-0.5 rounded-sm border text-[10px] font-semibold uppercase ${pr.cls}`}>{pr.label}</span>
            </SelectTrigger>
            <SelectContent>
              {PRIORITIES.map((p) => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={ticket.status} onValueChange={(v) => patchTicket({ status: v })}>
            <SelectTrigger className="w-44 h-8" data-testid="ticket-status-quick">
              <span className={`inline-flex px-1.5 py-0.5 rounded-sm border text-[10px] font-semibold uppercase ${st.cls}`}>{st.label}</span>
            </SelectTrigger>
            <SelectContent>
              {TICKET_STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={() => { setEditing(null); setParentTask(null); setDialogOpen(true); }}
            data-testid="ticket-add-task-btn">
            <Plus className="h-4 w-4 mr-1" /> Add task
          </Button>
          <Button size="sm" variant="outline" onClick={removeTicket}
            className="text-red-600 border-red-200 hover:bg-red-50"
            data-testid="ticket-delete-btn">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Tabs defaultValue="tasks">
        <TabsList>
          <TabsTrigger value="tasks" data-testid="ticket-tab-tasks">
            <ListChecks className="h-4 w-4 mr-1" /> Tasks ({tasks.length})
          </TabsTrigger>
          <TabsTrigger value="gantt" data-testid="ticket-tab-gantt">
            <GanttChartSquare className="h-4 w-4 mr-1" /> Gantt
          </TabsTrigger>
        </TabsList>
        <TabsContent value="tasks">
          <Card><CardContent className="p-4">
            <TaskTree tasks={tasks}
              onEdit={(t) => { setEditing(t); setParentTask(null); setDialogOpen(true); }}
              onAddSub={(t) => { setEditing(null); setParentTask(t); setDialogOpen(true); }}
              onDelete={deleteTask} onQuickStatus={quickStatus} onReorder={reorder} />
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="gantt">
          <Card><CardContent className="p-4">
            <GanttChart tasks={tasks} onUpdateDates={updateDates} />
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      <TaskDialog open={dialogOpen} onOpenChange={setDialogOpen}
        initial={editing} onSave={saveTask} assignees={assignees} />
    </div>
  );
}
